import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from '@/lib/axios'
import { toast } from 'sonner'
import { z } from 'zod'

export const localizedStringSchema = z.object({
    tr: z.string().min(1, 'Türkçe içerik zorunludur'),
    en: z.string().optional(),
    fr: z.string().optional(),
    es: z.string().optional(),
    ru: z.string().optional(),
    nl: z.string().optional(),
    ar: z.string().optional(),
})

export const pageSchema = z.object({
    _id: z.string().optional(),
    slug: z.string().min(1, 'Slug zorunludur (örn: kvkk)'),
    title: localizedStringSchema,
    content: localizedStringSchema,
    isActive: z.boolean().default(true),
    translate: z.boolean().optional(),
})

export type Page = z.infer<typeof pageSchema>

export function usePages() {
    const queryClient = useQueryClient()

    const pagesQuery = useQuery({
        queryKey: ['pages'],
        queryFn: async () => {
            const res = await axios.get<Page[]>('/pages')
            return res.data
        },
    })

    const createMutation = useMutation({
        mutationFn: async (data: Page) => {
            const res = await axios.post<Page>('/pages', data)
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pages'] })
            toast.success('Sayfa başarıyla eklendi')
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Sayfa eklenirken bir hata oluştu')
        },
    })

    const updateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<Page> }) => {
            const res = await axios.put<Page>(`/pages/${id}`, data)
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pages'] })
            toast.success('Sayfa başarıyla güncellendi')
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Sayfa güncellenirken bir hata oluştu')
        },
    })

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            await axios.delete(`/pages/${id}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pages'] })
            toast.success('Sayfa başarıyla silindi')
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Sayfa silinirken bir hata oluştu')
        },
    })

    return {
        pages: pagesQuery.data || [],
        isLoading: pagesQuery.isLoading,
        createPage: createMutation.mutate,
        isCreating: createMutation.isPending,
        updatePage: updateMutation.mutate,
        isUpdating: updateMutation.isPending,
        deletePage: deleteMutation.mutate,
        isDeleting: deleteMutation.isPending,
    }
}
